import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as RD from "reports/ReportDef";
import * as SR from "reports/measures/SeverityResolver";

/**
 * Calculates Work Status Improved (New) status from work measures.
 *
 * @param workStatusImprovedNewMeasures - Array of work measures
 * @returns WorkStatusImprovedNewStatus or null if no measures
 */
export function calculateWorkStatusImprovedNewStatus(
    workStatusImprovedNewMeasures: MD.Measures
): RD.WorkStatusImprovedNewStatus | null {
    if (workStatusImprovedNewMeasures.length === 0) {
        return null;
    }

    const mostRecentMeasure: MD.Measure = workStatusImprovedNewMeasures[workStatusImprovedNewMeasures.length - 1];

    // Handle N/A values (0) from new question format
    // 0 indicates "already working full duty" or "other" responses
    // These should not be included in the report
    if (mostRecentMeasure.value === 0) {
        return null; // N/A scenario - don't display
    }

    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label,
        citation: (mostRecentMeasure as any).citation
    } as RD.WorkStatusImprovedNewStatus;
}

/**
 * Calculates Return to Work (Old) status from work measures.
 *
 * @param returnToWorkOldMeasures - Array of work measures
 * @returns ReturnToWorkOldStatus or null if no measures
 */
export function calculateReturnToWorkOldStatus(
    returnToWorkOldMeasures: MD.Measures
): RD.ReturnToWorkOldStatus | null {
    if (returnToWorkOldMeasures.length === 0) {
        return null;
    }

    const mostRecentMeasure: MD.Measure = returnToWorkOldMeasures[returnToWorkOldMeasures.length - 1];
    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label,
        citation: (mostRecentMeasure as any).citation
    } as RD.ReturnToWorkOldStatus;
}

/**
 * Calculates Medication Reduction (New) from medication measures.
 *
 * @param medicationReductionNewMeasures - Array of medication measures
 * @returns MedicationReductionNewStatus or null if no measures
 */
export function calculateMedicationReductionNewStatus(
    medicationReductionNewMeasures: MD.Measures
): RD.MedicationReductionNewStatus | null {
    if (medicationReductionNewMeasures.length === 0) {
        return null;
    }

    const currentWeekValue = medicationReductionNewMeasures[medicationReductionNewMeasures.length - 1].value;

    const wasMedicationReduced = (measures: MD.Measures, currentWeekValue: number): number => {
        // Handle N/A values (-1) from new question format
        // -1 indicates "never took medication" or "other" responses
        const hasNAValue = (value: number): boolean => value === -1;

        // Filter out N/A values for analysis
        const validMeasures = measures.filter(m => !hasNAValue(m.value));

        // If all values are N/A, return N/A (represented as -1 or 0)
        if (validMeasures.length === 0) {
            return 0; // N/A scenario
        }

        // If current week is N/A, treat as "never took medication"
        if (hasNAValue(currentWeekValue)) {
            return 0; // N/A scenario
        }

        const areAllValuesZero = (measuresArray: MD.Measures): boolean => {
            if (measuresArray.length === 0) {
                return true;
            }
            return measuresArray.every(measure => measure.value === 0 || hasNAValue(measure.value));
        };

        // --- Conditions for "No" ---
        // 1. Answer value for "Medication" in the report week's Survey is 2 or higher, OR
        // 2. All answer values for "Medication" in the current and previous weeks are 0 (indicating patient never took medication).
        const isCurrentWeekValueTwoOrHigher = currentWeekValue >= 2;
        const areAllMeasuresZeroIncludingCurrent = areAllValuesZero(validMeasures);
        if (isCurrentWeekValueTwoOrHigher || areAllMeasuresZeroIncludingCurrent) {
            return 0;
        }

        // --- Conditions for "Yes" ---
        // 1. Answer value for "Medication" in the report week's Survey was 1 or lower, AND
        // 2. "Medication" values in previous weeks are not all 0
        const previousWeeksMeasures = validMeasures.slice(0, validMeasures.length - 1);
        const arePreviousWeeksNotAllZero = !areAllValuesZero(previousWeeksMeasures);

        if (arePreviousWeeksNotAllZero && currentWeekValue <= 1) {
            return 1;
        }

        // If none of the "No" conditions were met, and the "Yes" conditions were not fully met,
        // it implies `currentWeekValue` is 0 or 1, but there was no medication taken in previous weeks.
        // In this specific scenario, it should report "No" (as per the "never took medication" clause).
        return 0;
    };

    let mostRecentMeasure: MD.Measure = medicationReductionNewMeasures[medicationReductionNewMeasures.length - 1];
    mostRecentMeasure.value = wasMedicationReduced(medicationReductionNewMeasures, currentWeekValue);
    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label,
        citation: (mostRecentMeasure as any).citation
    } as RD.MedicationReductionNewStatus;
}

/**
 * Calculates Medication Reduction (Old) from medication measures.
 *
 * @param medicationREductionOldMeasures - Array of medication measures
 * @returns MedicationReductionOldStatus or null if no measures
 */
export function calculateMedicationReductionOldStatus(
    medicationREductionOldMeasures: MD.Measures
): RD.MedicationReductionOldStatus | null {
    if (medicationREductionOldMeasures.length === 0) {
        return null;
    }

    const currentWeekValue = medicationREductionOldMeasures[medicationREductionOldMeasures.length - 1].value;

    const wasMedicationReduced = (measures: MD.Measures, currentWeekValue: number): number => {
        const areAllValuesZero = (measuresArray: MD.Measures): boolean => {
            if (measuresArray.length === 0) {
                return true;
            }
            return measuresArray.every(measure => measure.value === 0);
        };

        // --- Conditions for "No" ---
        // 1. Answer value for "Medication" in the report week's Survey is 2 or higher, OR
        // 2. All answer values for "Medication" in the current and previous weeks are 0 (indicating patient never took medication).
        const isCurrentWeekValueTwoOrHigher = currentWeekValue >= 2;
        const areAllMeasuresZeroIncludingCurrent = areAllValuesZero(measures);
        if (isCurrentWeekValueTwoOrHigher || areAllMeasuresZeroIncludingCurrent) {
            return 0;
        }

        // --- Conditions for "Yes" ---
        // 1. Answer value for "Medication" in the report week's Survey was 1 or lower, AND
        // 2. "Medication" values in previous weeks are not all 0
        const previousWeeksMeasures = measures.slice(0, measures.length - 1);
        const arePreviousWeeksNotAllZero = !areAllValuesZero(previousWeeksMeasures);

        if (arePreviousWeeksNotAllZero) {
            return 1;
        }

        // If none of the "No" conditions were met, and the "Yes" conditions were not fully met,
        // it implies `currentWeekValue` is 0 or 1, but there was no medication taken in previous weeks.
        // In this specific scenario, it should report "No" (as per the "never took medication" clause).
        return 0;
    };

    let mostRecentMeasure: MD.Measure = medicationREductionOldMeasures[medicationREductionOldMeasures.length - 1];
    mostRecentMeasure.value = wasMedicationReduced(medicationREductionOldMeasures, currentWeekValue);
    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label,
        citation: (mostRecentMeasure as any).citation
    } as RD.MedicationReductionOldStatus;
}
